import { useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { aTurnoDePanel, ordenarTurnos } from '../lib/turnos';

/**
 * Los turnos del negocio, en vivo.
 *
 * El panel cargaba la agenda una sola vez, al abrirla. O sea que un turno que
 * entraba por la página pública a las 10:05 no existía para el negocio hasta que
 * alguien apretara F5 — y nadie refresca una pantalla que no tiene motivos para
 * pensar que está desactualizada. Acá el navegador se suscribe a la consulta y
 * Firestore le empuja cada alta, cada cambio de estado y cada cancelación.
 *
 *
 * POR QUÉ FIRESTORE Y NO UNA CONEXIÓN CONTRA NUESTRO BACKEND
 *
 * Empujar desde Express (SSE o WebSocket) encajaría mejor con la arquitectura
 * —todo lo demás pasa por la API—, pero el backend corre en el plan gratuito de
 * Render, que DUERME el servicio a los 15 minutos sin tráfico (ver render.yaml).
 * Una conexión que tiene que quedarse abierta contra un servicio que se apaga y
 * tarda 30-60 segundos en despertar es el cimiento equivocado. Firestore no se
 * duerme, y los turnos siguen entrando igual porque quien los crea es la página
 * pública, que sí despierta al backend.
 *
 * Esto NO abre la mano con las escrituras: las reglas dejan `read` y nada más.
 * El cliente sigue sin poder crear ni modificar una reserva; para eso está la
 * API con el admin SDK, como siempre.
 *
 *
 * NO REEMPLAZA AL FETCH, SE SUMA
 *
 * Devuelve `null` en `turnos` mientras no haya recibido su primera respuesta, y
 * también si el listener falla. Quien lo usa se queda con lo que cargó por
 * `GET /api/bookings`, que sigue funcionando igual. Así, si las reglas todavía
 * no se desplegaron o App Check rechaza la lectura, el panel no queda vacío: se
 * queda como estaba antes de todo esto.
 */
export default function useTurnosEnVivo(businessId, { activo = true } = {}) {
  const [turnos, setTurnos] = useState(null);
  const [error, setError] = useState(null);
  // El instante en que se recibió la última respuesta, para poder mostrar
  // "actualizado hace un rato" si alguna vez hace falta. No dispara re-render
  // por sí solo.
  const ultimaRespuesta = useRef(null);

  useEffect(() => {
    if (!activo || !businessId) return undefined;

    /**
     * El `where` no es opcional ni una optimización: Firestore evalúa las reglas
     * contra la CONSULTA, no contra los documentos que devolvería. Sin este
     * filtro la consulta pide "todas las reservas", la regla no puede garantizar
     * que todas sean de este negocio y rechaza la suscripción entera — aunque el
     * negocio fuera dueño de cada una de ellas.
     */
    const consulta = query(
      collection(db, 'bookings'),
      where('businessId', '==', businessId)
    );

    const cancelar = onSnapshot(
      consulta,
      (snapshot) => {
        const crudos = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        // La misma forma y el mismo orden que arma el camino del fetch: los dos
        // pasan por lib/turnos.js justamente para que un turno no se vea
        // distinto según por dónde llegó.
        setTurnos(ordenarTurnos(crudos.map(aTurnoDePanel)));
        setError(null);
        ultimaRespuesta.current = Date.now();
      },
      (err) => {
        // Los dos motivos realistas son `permission-denied` (las reglas todavía
        // no se desplegaron, o la consulta no coincide con la regla) y App Check
        // rechazando el token. En los dos casos no hay nada que reintentar solo:
        // se avisa y el panel sigue con lo que trajo la API.
        console.error('useTurnosEnVivo: el listener falló, se sigue con el fetch.', err);
        setError(err);
        setTurnos(null);
      }
    );

    return () => cancelar();
  }, [businessId, activo]);

  return { turnos, error, ultimaRespuesta };
}
